import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiClientRequestTypeEnum, type ApiClientStatus, ThingsApiClient } from '@lifepowr/components/src/utils/api-client';

/**
 * RealTimeApiClient is responsible for triggering the real-time device measurements via the devices API and
 * ensuring that only one API call is made per device, essentially debouncing multiple calls to it.
 *
 * It uses an observer pattern to notify registered listeners about the status of the API call.
 *
 * @note It supports only ONE device at a time.
 */
class RealTimeApiClient extends ThingsApiClient<any> {
  constructor() {
    super(true, ApiClientRequestTypeEnum.POST);
  }

  protected async preApiInvocationAction(): Promise<any> {
    return {};
  }

  protected async postApiInvocationAction(_: any): Promise<void> {
    /**/
  }

  protected async apiInvocationErrorAction(_: any): Promise<void> {
    /**/
  }

  protected getApiUri(): string {
    return `/devices/${this.thingName}/realTime`;
  }
}

const realTimeApiClient = new RealTimeApiClient();

export function useRealTimeApiHook(realTimeDataAreActive: boolean) {
  const [realTimeApiClientStatus, setRealTimeApiClientStatus] = useState<ApiClientStatus<any>>(realTimeApiClient.getStatus());
  const thingNameRef = useRef<string | null>(null);
  const [areRealTimeDataActive, setRealTimeDataActive] = useState<boolean>(realTimeDataAreActive);

  useEffect(() => {
    return realTimeApiClient.subscribe((newRealTimeApiClientStatus) => {
      setRealTimeApiClientStatus({ ...newRealTimeApiClientStatus });
    });
  }, []);

  const setThingName = useCallback(async (thingName: string): Promise<void> => {
    thingNameRef.current = thingName;

    if (!thingNameRef.current) {
      return;
    }

    return realTimeApiClient.fetchData(thingName).catch((err) => {
      console.error('Error triggering real-time data:', err);
    });
  }, []);

  const triggerRealTimeData = useCallback(async (): Promise<void> => {
    if (areRealTimeDataActive) {
      return;
    }

    if (thingNameRef.current) {
      realTimeApiClient.resetState();

      return realTimeApiClient.fetchData(thingNameRef.current).catch((err) => {
        console.error('Error triggering real-time data:', err);
      });
    }
  }, []);

  return {
    areRealTimeDataActive,
    realTimeApiClientStatus,
    setThingName,
    triggerRealTimeData,
    setRealTimeDataActive,
  };
}
